import useAuthStore from '@/src/hooks/auth';
import { useCurrentDeviceStore } from '@/src/modules/pricing/hooks/useCurrentDeviceStore';
import { useIsRevenueCatAvailable } from '@/src/modules/pricing/hooks/useIsRevenueCatAvailable';
import type { PurchaseType } from '@fabric-app/business/pricing/pricing.types';
import { PurchaseError, PurchaseErrorCode } from '@fabric-app/business/pricing/pricing.types';
import { useInvalidateQuerySubscription } from '@fabric-app/business/pricing/queries/useQuerySubscription';
import { confirm, toast } from '@fabric-app/business/store/alerts';
import { useWoody } from '@fabric-app/business/woody/woody';
import { getCompatEnv } from '@fabric-app/compat/capabilities/environment';
import { openExternal } from '@fabric-app/ui/components/Link/LinkExternal';
import type { SubscriptionTier } from '@fabric/woody-client';
import { useMutation } from '@tanstack/react-query';
import { mutate } from 'swr';

type MutationVariables = {
  priceId: string;
  type: PurchaseType;

  optimistic: {
    tier: SubscriptionTier;
    billingCycle: string;
  };
};

type WebPurchaseResult = {
  deferred: boolean;
};

/**
 * Will either send the user to the checkout page or update the user's subscription directly using woody.
 * **DON'T USE THIS DIRECTLY**: @see useMutationPurchase instead which has extra conditions and checks.
 */
export const useMutationWebPurchase = () => {
  const { client } = useWoody();
  const isRevenueCatAvailable = useIsRevenueCatAvailable();
  const currentDeviceStore = useCurrentDeviceStore();
  const querySubscriptionInvalidate = useInvalidateQuerySubscription();

  const { user, setUser } = useAuthStore((state) => {
    return {
      user: state.user,
      setUser: state.setUser,
    };
  });

  const mutation = useMutation({
    mutationFn: async ({ priceId, type }: MutationVariables): Promise<WebPurchaseResult> => {
      if (isRevenueCatAvailable) {
        throw new PurchaseError(PurchaseErrorCode.GENERIC, currentDeviceStore);
      }

      if (type === 'first') {
        const url = new URL(getCompatEnv().WOODY_API_URL + "/v2/subscription/checkout");
        url.searchParams.append('priceId', priceId);
        url.searchParams.append('redirect', 'true');

        openExternal(url.toString());
        return { deferred: true };
      }

      const confirmed = await confirm({
        title: `Confirm ${type === 'upgrade' ? 'upgrade' : 'downgrade'}?`,
        content: `Your plan will be immediately ${type === 'upgrade' ? 'upgraded' : 'downgraded'}.`,
        confirmLabel: 'Yes, continue',
        cancelLabel: 'Cancel',
      });

      if (!confirmed) {
        throw new PurchaseError(PurchaseErrorCode.CANCELLED, currentDeviceStore);
      }

      await client.v2('/v2/subscription', {
        method: 'patch',
        body: {
          priceId,
        },
      });

      return { deferred: false };
    },
    onSuccess: (result, { priceId, optimistic }) => {
      if (result.deferred) return;

      toast({
        content: 'Your subscription has been updated.',
      });

      if (user)
        setUser({
          ...user,
          subscription: {
            ...user.subscription,
            tier: optimistic.tier,
            billingCycle: optimistic.billingCycle,
            store: 'stripe',
            storeId: priceId,
          },
        });

      /**
       * unfortunately server returns old value, probably takes time to process (update stripe and what not)
       * so we need to wait a bit before revalidating the woody-user
       */
      setTimeout(() => {
        mutate('woody-user');
      }, 1000);
      querySubscriptionInvalidate();
    },
  });

  return mutation;
};
